import { useEffect, useRef, useState } from "react";
import { Loader2, Search, Link2Off, Globe, CornerDownLeft, Type, ExternalLink, MousePointerClick, FolderOpen, FileText } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

/** One place to make any link: name it, then either search this KB by article
 *  name or paste a web address. Opens on ⌘K / Ctrl-K with the selected words
 *  already filled in as the link text, so selecting "Export invoices" and
 *  pressing the shortcut finds the article before you've typed anything.
 *  Article links carry the article id, so they survive a rename. */
export function KbLinkDialog({ open, onOpenChange, onPickArticle, onPickUrl, onRemove,
                               initialText = "", currentHref = "", currentNewTab = false,
                               currentButton = false }) {
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState("");
  const [newTab, setNewTab] = useState(false);
  const [asButton, setAsButton] = useState(false);
  const [active, setActive] = useState(0);
  const touchedText = useRef(false);
  const touchedNewTab = useRef(false);

  // An external address almost always wants a new tab, so tick it as soon as
  // one is typed — until the editor sets the switch themselves, after which
  // their choice stands. KB links are left in the same tab.
  useEffect(() => {
    if (touchedNewTab.current) return;
    const v = url.trim();
    const external = /^(https?:)?\/\//i.test(v)
      && !v.toLowerCase().includes(window.location.host.toLowerCase());
    setNewTab(external);
  }, [url]);

  useEffect(() => {
    if (!open) return;
    setText(initialText.slice(0, 200));
    setQ(initialText.slice(0, 60));
    setUrl(/^\/kb\/(a|c)\//.test(currentHref) ? "" : currentHref);
    setNewTab(currentNewTab);
    setAsButton(currentButton);
    setActive(0);
    touchedText.current = false;
    touchedNewTab.current = currentNewTab;
  }, [open, initialText, currentHref, currentNewTab, currentButton]);

  // 269 categories is small enough to filter here, and it keeps the list
  // instant while you type.
  useEffect(() => {
    if (!open || cats.length) return;
    api.get("/kb/tree")
      .then((r) => setCats(r.data || []))
      .catch(() => setCats([]));
  }, [open, cats.length]);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      setLoading(true);
      api.get("/kb/manage/articles", { params: { q, limit: 20, sort: "name" } })
        .then((r) => { setRows(r.data.articles || []); setActive(0); })
        .catch(() => setRows([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  const byId = Object.fromEntries(cats.map((c) => [c.cat_id, c]));
  const trail = (c) => {
    const names = [];
    let node = byId[c.parent_id];
    for (let i = 0; node && i < 4; i += 1) { names.unshift(node.name); node = byId[node.parent_id]; }
    return names.join(" › ");
  };
  const needle = q.trim().toLowerCase();
  const catHits = (needle
    ? cats.filter((c) => c.name.toLowerCase().includes(needle)
        || (c.slug || "").includes(needle.replace(/\s+/g, "-")))
    : cats.filter((c) => (c.article_count_deep || 0) > 0))
    .sort((a, b) => (b.article_count_deep || 0) - (a.article_count_deep || 0))
    .slice(0, 6);
  // Categories first: there are far fewer of them, and someone who typed a
  // category name doesn't want to scroll past 20 articles to find it.
  const results = [
    ...catHits.map((c) => ({ kind: "category", key: `c-${c.cat_id}`, item: c })),
    ...rows.map((a) => ({ kind: "article", key: `a-${a.article_id}`, item: a })),
  ];

  // An untouched text box follows the article you pick — name it yourself and
  // we stop guessing.
  const pick = (a) => {
    onPickArticle(a, { text: (text.trim() || a.name), newTab, asButton });
    onOpenChange(false);
  };
  const pickCategory = (c) => {
    onPickUrl(`/kb/c/${c.slug}`, { text: text.trim() || c.name, newTab, asButton });
    onOpenChange(false);
  };
  const choose = (r) => (r.kind === "category" ? pickCategory(r.item) : pick(r.item));
  const applyAddress = () => {
    const v = url.trim();
    if (!v) return;
    const href = /^(https?:|mailto:|tel:|\/|#)/i.test(v) ? v : `https://${v}`;
    onPickUrl(href, { text: text.trim() || href, newTab, asButton });
    onOpenChange(false);
  };

  const onSearchKeys = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && results[active]) { e.preventDefault(); choose(results[active]); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl gap-3" data-testid="kb-link-dialog">
        <DialogHeader><DialogTitle>Add a link</DialogTitle></DialogHeader>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">Link text</span>
          <div className="relative">
            <Type className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <Input value={text} data-testid="kb-link-dialog-text"
              onChange={(e) => { touchedText.current = true; setText(e.target.value); }}
              placeholder="The words the reader sees"
              className="h-9 pl-8 text-sm" />
          </div>
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
            Link to an article or a category in this Knowledge Base
          </span>
          {/^\/kb\/(a|c)\//.test(currentHref) && (
            <span className="mb-1 inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:text-slate-300"
              data-testid="kb-link-dialog-current">
              Currently points at {currentHref}
            </span>
          )}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <Input autoFocus data-testid="kb-link-dialog-search" value={q} onKeyDown={onSearchKeys}
              placeholder="Search articles and categories…" onChange={(e) => setQ(e.target.value)}
              className="h-9 pl-8 text-sm" />
          </div>
        </label>

        {loading ? (
          <div className="flex h-32 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>
        ) : (
          <ul className="max-h-[34vh] space-y-0.5 overflow-y-auto" data-testid="kb-link-dialog-results">
            {results.length === 0 && (
              <li className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">
                Nothing matches “{q}”. Paste a web address below instead.
              </li>
            )}
            {results.map((r, i) => {
              const isCat = r.kind === "category";
              const it = r.item;
              return (
                <li key={r.key}>
                  <button type="button"
                    data-testid={`kb-link-dialog-pick-${isCat ? "cat-" : ""}${it.slug}`}
                    onMouseEnter={() => setActive(i)} onClick={() => choose(r)}
                    className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors ${
                      i === active ? "bg-primary/10" : "hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                    {isCat
                      ? <FolderOpen className="h-4 w-4 shrink-0 text-amber-500" />
                      : <FileText className="h-4 w-4 shrink-0 text-slate-300 dark:text-slate-600" />}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-100">{it.name}</span>
                      <span className="block truncate text-xs text-slate-400 dark:text-slate-500">
                        {isCat
                          ? `Category · ${it.article_count_deep || 0} article${(it.article_count_deep || 0) === 1 ? "" : "s"}${trail(it) ? ` · ${trail(it)}` : ""}`
                          : `/kb/a/${it.slug}${it.is_draft ? " · draft" : ""}`}
                      </span>
                    </span>
                    {i === active && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-primary" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="border-t border-slate-100 dark:border-slate-800 pt-3">
          <span className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">
            …or paste an external web address
          </span>
          <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Globe className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <Input value={url} onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyAddress(); } }}
              placeholder="https://…" data-testid="kb-link-dialog-url"
              className="h-8 pl-8 text-xs" />
          </div>
          <Button size="sm" className="h-8 text-xs" onClick={applyAddress} disabled={!url.trim()}
            data-testid="kb-link-dialog-url-submit">Use address</Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 dark:border-slate-800 pt-3">
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
              <Switch checked={newTab} data-testid="kb-link-dialog-newtab"
                onCheckedChange={(v) => { touchedNewTab.current = true; setNewTab(v); }} />
              <ExternalLink className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" /> Open in a new tab
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
              <Switch checked={asButton} onCheckedChange={setAsButton} data-testid="kb-link-dialog-button" />
              <MousePointerClick className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" /> Style it as a button
            </label>
          </div>
          {currentHref && (
            <Button size="sm" variant="outline" className="h-8 text-xs"
              onClick={() => { onRemove?.(); onOpenChange(false); }}
              data-testid="kb-link-dialog-remove">
              <Link2Off className="mr-1.5 h-3.5 w-3.5" /> Remove link
            </Button>
          )}
        </div>

        {asButton && (
          <p className="-mt-1 text-[11px] text-slate-400 dark:text-slate-500" data-testid="kb-link-dialog-button-hint">
            Buttons stand out on the page and stay a link everywhere else — in search results,
            in print and in exports.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
